import React, { useState, useEffect, useRef } from 'react';
import { subscribeDokumente, uploadDokument, deleteDokument } from './dokumenteStorage';
import MobileBottomSheet from './components/MobileBottomSheet';
import './MeineDokumente.css';

/* ─── Palette (matches design proto tokens) ─────────────────────────────── */
const C = {
  amber:       '#FFBC00',
  amberSoft:   'rgba(255,188,0,0.14)',
  amberBorder: 'rgba(255,188,0,0.30)',
  teal:        '#004D40',
  tealMid:     '#006B5A',
  tealSoft:    'rgba(0,77,64,0.08)',
  tealBorder:  'rgba(0,77,64,0.12)',
  cream:       '#F5F2E9',
  cream2:      '#F0EAD8',
  elevated:    '#FDFAF4',
  ink:         '#0F1E1A',
  text:        '#1B2D28',
  muted:       '#5B6964',
  faint:       '#8B9590',
  borderFaint: 'rgba(0,0,0,0.05)',
  borderSubtle:'rgba(0,0,0,0.07)',
  shadowCard:  '0 1px 2px rgba(0,77,64,0.06), 0 4px 16px rgba(0,77,64,0.08)',
  fontDisplay: "'DM Serif Display', Georgia, serif",
  fontBody:    "'Geist', -apple-system, system-ui, sans-serif",
};

/* ─── Category config ────────────────────────────────────────────────────── */
const KATEGORIEN = [
  { id: 'cv',              label: 'Lebensläufe',    desc: '3 Versionen',          size: 'featured' },
  { id: 'anschreiben',     label: 'Anschreiben',    desc: 'Motivationsschreiben', size: 'wide'     },
  { id: 'zeugnisse',       label: 'Zeugnisse',      desc: 'Schul- & Arbeit',      size: 'tile'     },
  { id: 'fotos',           label: 'Fotos',          desc: 'Bewerbungsfotos',      size: 'tile'     },
  { id: 'bescheinigungen', label: 'Belege',         desc: 'Immatrikulation u.ä.', size: 'tile'     },
  { id: 'zertifikate',     label: 'Zertifikate',    desc: 'Sprach- & IT-Zerts',   size: 'tile'     },
  { id: 'sonstige',        label: 'Sonstige',       desc: 'Führerschein, ID — leer', size: 'wide'  },
];

const DOC_ACCEPT = { cv: '.pdf,.doc,.docx', anschreiben: '.pdf,.doc,.docx', fotos: 'image/jpeg,image/png,image/webp', zeugnisse: '.pdf,.doc,.docx', bescheinigungen: '.pdf,.doc,.docx', zertifikate: '.pdf,.doc,.docx', sonstige: '.pdf,.doc,.docx' };
const FOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const DOC_TYPES  = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

/* ─── Tiny icons ─────────────────────────────────────────────────────────── */
const Ic = ({ d, size = 16, s = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={s} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);
const IcDoc    = p => <Ic {...p} d={<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="M9 13h6M9 17h4"/></>} />;
const IcMail   = p => <Ic {...p} d={<><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></>} />;
const IcCheck  = p => <Ic {...p} d={<><path d="M9 11l3 3 8-8"/><path d="M21 12v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></>} />;
const IcImg    = p => <Ic {...p} d={<><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="11" r="2"/><path d="M21 17l-5-5-9 9"/></>} />;
const IcClip   = p => <Ic {...p} d={<><rect x="6" y="3" width="12" height="18" rx="2"/><path d="M9 3v2h6V3"/><path d="M9 11h6M9 15h4"/></>} />;
const IcBadge  = p => <Ic {...p} d={<><path d="M12 15a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"/><path d="M8 13l-3 7 5-3 2 3 2-3 5 3-3-7"/></>} />;
const IcFolder = p => <Ic {...p} d={<><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></>} />;
const IcPlus   = p => <Ic {...p} d={<><path d="M12 5v14M5 12h14"/></>} />;
const IcTrash  = p => <Ic {...p} d={<><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></>} />;
const IcDown   = p => <Ic {...p} d={<><path d="M12 4v16m-8-8 8 8 8-8"/></>} />;
const IcX      = p => <Ic {...p} d={<><path d="M18 6 6 18M6 6l12 12"/></>} />;
const IcChev   = p => <Ic {...p} d={<path d="M9 18l6-6-6-6"/>} />;

const ICONS = { cv: IcDoc, anschreiben: IcMail, zeugnisse: IcCheck, fotos: IcImg, bescheinigungen: IcClip, zertifikate: IcBadge, sonstige: IcFolder };

/* ─── Desktop animation components (same as PC version) ─────────────────── */
const CheckSvg = () => (
  <svg viewBox="0 0 10 10" fill="none" width="7" height="7">
    <polyline points="1.5,5 4,7.5 8.5,2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MiniCvPage = () => (
  <div className="md-mp">
    <div className="md-mp-head">
      <div className="md-mp-avatar"></div>
      <div><div className="md-mp-name"></div><div className="md-mp-sub-line"></div></div>
    </div>
    <div className="md-mp-rule"></div>
    <div className="md-mp-section"></div>
    <div className="md-mp-line"></div>
    <div className="md-mp-line short"></div>
    <div className="md-mp-line"></div>
    <div className="md-mp-section"></div>
    <div className="md-mp-line"></div>
    <div className="md-mp-line short"></div>
    <div className="md-mp-line faint"></div>
  </div>
);

const AnimLebenslauf = () => (
  <div className="md-an-lv">
    <div className="md-an-lv-paper md-an-lv-3"><MiniCvPage /></div>
    <div className="md-an-lv-paper md-an-lv-2"><MiniCvPage /></div>
    <div className="md-an-lv-paper md-an-lv-1"><MiniCvPage /></div>
  </div>
);

const AnimAnschreiben = () => (
  <div className="md-an-env">
    <div className="md-an-env-back"></div>
    <div className="md-an-env-stamp">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="5" width="18" height="14" rx="2"/>
        <path d="m3 7 9 6 9-6"/>
      </svg>
    </div>
    <div className="md-an-env-letter">
      <div className="md-an-env-letter-from">Max Mustermann</div>
      <div className="md-an-env-letter-to">An: CV-Flow AG</div>
      <div className="md-an-env-letter-rule"></div>
      <div className="md-an-env-letter-line"></div>
      <div className="md-an-env-letter-line short"></div>
      <div className="md-an-env-letter-line"></div>
      <div className="md-an-env-letter-line tiny"></div>
      <div className="md-an-env-letter-sig"></div>
    </div>
    <div className="md-an-env-front"></div>
  </div>
);

const AnimZeugnisse = () => (
  <div className="md-an-zeu">
    <div className="md-an-zeu-roll">
      <div className="md-an-zeu-head">Abiturzeugnis</div>
      <div className="md-an-zeu-grade">1,7</div>
      <div className="md-an-zeu-school">Goethe-Gymnasium · 2019</div>
      <div className="md-an-zeu-seal"><CheckSvg /></div>
    </div>
  </div>
);

const AnimZertifikate = () => (
  <div className="md-an-zer">
    <div className="md-an-zer-cert">
      <div className="md-an-zer-title">Zertifikat</div>
      <div className="md-an-zer-line"></div>
      <div className="md-an-zer-name-real">IELTS · C1</div>
      <div className="md-an-zer-line short"></div>
      <div className="md-an-zer-seal">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20 6 9 17l-5-5"/></svg>
      </div>
    </div>
    <div className="md-an-zer-cert">
      <div className="md-an-zer-ribbon"></div>
      <div className="md-an-zer-title">Zertifikat</div>
      <div className="md-an-zer-line"></div>
      <div className="md-an-zer-name-real">AWS SAA-C03</div>
      <div className="md-an-zer-line short"></div>
      <div className="md-an-zer-seal">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20 6 9 17l-5-5"/></svg>
      </div>
    </div>
    <div className="md-an-zer-cert">
      <div className="md-an-zer-title">Zertifikat</div>
      <div className="md-an-zer-line"></div>
      <div className="md-an-zer-name-real">Scrum · PSM I</div>
      <div className="md-an-zer-line short"></div>
      <div className="md-an-zer-seal">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20 6 9 17l-5-5"/></svg>
      </div>
    </div>
  </div>
);

const AnimBescheinigungen = () => (
  <div className="md-an-bes">
    <div className="md-an-bes-doc">
      <div className="md-an-bes-header"></div>
      <div className="md-an-bes-lines">
        <div className="md-an-bes-line"></div>
        <div className="md-an-bes-line"></div>
        <div className="md-an-bes-line short"></div>
        <div className="md-an-bes-line"></div>
        <div className="md-an-bes-line short2"></div>
      </div>
    </div>
    <div className="md-an-bes-stamp">
      <div className="md-an-bes-stamp-ring"></div>
      <div className="md-an-bes-stamp-core">
        <div className="md-an-bes-stamp-l"></div>
        <div className="md-an-bes-stamp-l s"></div>
        <div className="md-an-bes-stamp-l"></div>
      </div>
    </div>
  </div>
);

const AnimFotos = () => (
  <div className="md-an-fot">
    <div className="md-an-fot-card md-an-fot-c1"><div className="md-an-fot-img"></div></div>
    <div className="md-an-fot-card md-an-fot-c2"><div className="md-an-fot-img"></div></div>
    <div className="md-an-fot-card md-an-fot-c3"><div className="md-an-fot-img"></div></div>
    <div className="md-an-fot-more">+3</div>
  </div>
);

const AnimSonstige = () => (
  <div className="md-an-son">
    <div className="md-an-son-back"></div>
    <div className="md-an-son-tab"></div>
    <div className="md-an-son-doc"></div>
    <div className="md-an-son-front"></div>
  </div>
);

const TILE_ANIMS = {
  cv: AnimLebenslauf, anschreiben: AnimAnschreiben, zeugnisse: AnimZeugnisse,
  fotos: AnimFotos, bescheinigungen: AnimBescheinigungen, zertifikate: AnimZertifikate, sonstige: AnimSonstige,
};

/* ─── Progress ring chip ──────────────────────────────────────────────────── */
function ProgressChip({ total, filled }) {
  const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
  const deg = Math.round(pct * 3.6);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.elevated, border: `1px solid ${C.borderFaint}`, borderRadius: 9999, padding: '5px 12px 5px 6px', width: 'fit-content' }}>
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: `conic-gradient(${C.amber} 0 ${deg}deg, rgba(0,77,64,0.08) ${deg}deg 360deg)`, display: 'grid', placeItems: 'center' }}>
        <div style={{ width: 13, height: 13, borderRadius: '50%', background: C.elevated }} />
      </div>
      <div style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.teal, fontWeight: 600 }}>
        {pct}<span style={{ textTransform: 'none', letterSpacing: 0, color: C.muted, marginLeft: 1 }}>%</span> &nbsp;<b style={{ color: C.ink }}>komplett</b>
      </div>
    </div>
  );
}

/* ─── Category card ───────────────────────────────────────────────────────── */
function KatCard({ kat, count, onPress }) {
  const Icon = ICONS[kat.id] || IcDoc;
  const isFeatured = kat.size === 'featured';
  const isWide     = kat.size === 'wide';
  const height     = isFeatured ? 300 : isWide ? 160 : 170;

  return (
    <div
      onClick={onPress}
      style={{
        position: 'relative',
        background: C.elevated,
        borderRadius: 18,
        border: `1px solid ${C.borderFaint}`,
        boxShadow: C.shadowCard,
        overflow: 'hidden',
        height,
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {/* icon chip */}
      <div style={{ position: 'absolute', top: 10, left: 10, width: 24, height: 24, borderRadius: 7, background: C.cream, border: `1px solid ${C.borderSubtle}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.teal, zIndex: 2 }}>
        <Icon size={12} />
      </div>

      {/* count badge or add pill */}
      <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}>
        {count > 0 ? (
          <div style={{ minWidth: 20, height: 20, borderRadius: 10, background: C.teal, color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px' }}>{count}</div>
        ) : (
          <div style={{ color: C.faint }}><IcPlus size={13} /></div>
        )}
      </div>

      {/* illustration area */}
      <div style={{ position: 'absolute', top: 42, left: 0, right: 0, bottom: 58, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ zoom: isFeatured ? 0.52 : isWide ? 0.42 : 0.55, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {TILE_ANIMS[kat.id] && React.createElement(TILE_ANIMS[kat.id])}
        </div>
      </div>

      {/* footer */}
      <div style={{ position: 'absolute', left: 14, right: 14, bottom: 13, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 6, zIndex: 2 }}>
        <div>
          <div style={{ fontFamily: C.fontDisplay, fontSize: isFeatured ? 24 : 18, lineHeight: 1.05, color: C.ink }}>{kat.label}</div>
          <div style={{ marginTop: 3, fontSize: 10.5, color: C.muted }}>{count > 0 ? `${count} Datei${count !== 1 ? 'en' : ''}` : kat.desc}</div>
        </div>
        <div style={{ width: 22, height: 22, borderRadius: '50%', background: C.cream, border: `1px solid ${C.borderSubtle}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.teal, flexShrink: 0 }}>
          <IcChev size={11} />
        </div>
      </div>
    </div>
  );
}


/* ─── Relative date formatter ─────────────────────────────────────────────── */
function formatDatum(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d) / 86400000);
  if (diff === 0) return 'heute';
  if (diff === 1) return 'gestern';
  if (diff < 7)  return `vor ${diff} Tagen`;
  return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
}

/* ─── Generated doc item ──────────────────────────────────────────────────── */
function GeneriertItem({ stelle, typ, onStelleOeffnen }) {
  const name  = stelle.name || (typ === 'cv' ? 'Lebenslauf' : 'Anschreiben');
  const datum = formatDatum(stelle.generiert_am || stelle.erstellt_am);
  return (
    <div
      onClick={() => onStelleOeffnen?.(stelle)}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${C.borderFaint}`, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
    >
      <div style={{ width: 36, height: 36, borderRadius: 8, background: C.tealSoft, border: `1px solid ${C.tealBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.teal, flexShrink: 0 }}>
        {typ === 'cv' ? <IcDoc size={16} /> : <IcMail size={16} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
        {datum && <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{datum}</div>}
      </div>
      <div style={{ color: C.faint, flexShrink: 0 }}>
        <IcChev size={14} />
      </div>
    </div>
  );
}

/* ─── File list item ──────────────────────────────────────────────────────── */
function DokumentItem({ dok, onDelete }) {
  const isImg = dok.type?.startsWith('image/');
  const ext   = dok.filename?.split('.').pop()?.toUpperCase() || 'DOC';

  function formatSize(b) {
    if (!b) return '';
    if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${C.borderFaint}` }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: C.amberSoft, border: `1px solid ${C.amberBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.teal, flexShrink: 0 }}>
        {isImg ? <IcImg size={16} /> : <IcDoc size={16} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dok.filename || 'Dokument'}</div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{ext} · {formatSize(dok.size)}</div>
      </div>
      <a href={dok.url} target="_blank" rel="noopener noreferrer" style={{ width: 32, height: 32, borderRadius: 8, background: C.tealSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.teal, flexShrink: 0, textDecoration: 'none' }}>
        <IcDown size={14} />
      </a>
      <button onClick={() => onDelete(dok)} style={{ width: 32, height: 32, borderRadius: 8, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.faint, WebkitTapHighlightColor: 'transparent' }}>
        <IcTrash size={14} />
      </button>
    </div>
  );
}

/* ─── Category detail bottom sheet ───────────────────────────────────────── */
function KatSheet({ kat, dokumente, stellen, onClose, onUpload, onStelleOeffnen }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const Icon = ICONS[kat.id] || IcDoc;
  const files = dokumente.filter(d => d.kategorie === kat.id);

  const generiert = kat.id === 'cv'
    ? stellen.filter(s => s.cv_data || s.cv_rohtext).sort((a, b) => new Date(b.generiert_am || b.erstellt_am || 0) - new Date(a.generiert_am || a.erstellt_am || 0))
    : kat.id === 'anschreiben'
    ? stellen.filter(s => s.anschreiben || s.anschreiben_rohtext).sort((a, b) => new Date(b.generiert_am || b.erstellt_am || 0) - new Date(a.generiert_am || a.erstellt_am || 0))
    : [];

  const totalCount = generiert.length + files.length;

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (kat.id === 'fotos') {
      if (!FOTO_TYPES.includes(file.type)) { setError('Nur JPEG, PNG oder WebP erlaubt.'); if (inputRef.current) inputRef.current.value = ''; return; }
    } else {
      if (!DOC_TYPES.includes(file.type)) { setError('Nur PDF oder Word-Dateien (.pdf, .doc, .docx) erlaubt.'); if (inputRef.current) inputRef.current.value = ''; return; }
    }
    setUploading(true); setError('');
    try { await onUpload(kat.id, file); }
    catch (err) { setError(err.message || 'Upload fehlgeschlagen'); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  }

  return (
    <div style={{ padding: '4px 20px 32px', fontFamily: C.fontBody }}>
      {/* sheet header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{ width: 44, height: 44, borderRadius: 13, background: C.amberSoft, border: `1px solid ${C.amberBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.teal }}>
          <Icon size={20} />
        </div>
        <div>
          <div style={{ fontFamily: C.fontDisplay, fontSize: 22, color: C.ink, lineHeight: 1.1 }}>{kat.label}</div>
          <div style={{ fontSize: 12, color: C.muted }}>{totalCount} Datei{totalCount !== 1 ? 'en' : ''}</div>
        </div>
      </div>

      {/* upload button */}
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        style={{
          width: '100%', padding: '12px 20px', borderRadius: 12,
          background: uploading ? C.cream2 : C.amber,
          border: 'none', cursor: uploading ? 'not-allowed' : 'pointer',
          fontFamily: C.fontBody, fontSize: 14, fontWeight: 700,
          color: C.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          WebkitTapHighlightColor: 'transparent', marginBottom: 16,
          boxShadow: uploading ? 'none' : '0 4px 14px rgba(255,188,0,0.4)',
        }}
      >
        {uploading ? 'Wird hochgeladen…' : <><IcPlus size={16} /> Datei hochladen</>}
      </button>
      <input ref={inputRef} type="file" accept={DOC_ACCEPT[kat.id] || '*'} onChange={handleFile} style={{ display: 'none' }} />

      {error && (
        <div style={{ background: 'rgba(200,30,30,0.08)', color: '#c81e1e', fontSize: 12, padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>{error}</div>
      )}

      {/* file list */}
      {totalCount === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: C.faint }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: C.tealSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.teal, margin: '0 auto 12px' }}>
            <Icon size={22} />
          </div>
          <div style={{ fontSize: 14, color: C.muted }}>Noch keine Dateien in dieser Kategorie</div>
          <div style={{ fontSize: 12, color: C.faint, marginTop: 4 }}>Lade deine erste Datei hoch</div>
        </div>
      ) : (
        <div>
          {generiert.map(s => <GeneriertItem key={s.id} stelle={s} typ={kat.id} onStelleOeffnen={onStelleOeffnen} />)}
          {files.map(d => <DokumentItem key={d.id} dok={d} onDelete={async (dok) => { try { await deleteDokument(dok); } catch {} }} />)}
        </div>
      )}
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────────────────────── */
export default function MobileMeineDokumente({ nutzer, stellen = [], aktivesProfile, onStelleOeffnen }) {
  const [dokumente, setDokumente] = useState([]);
  const [aktiveKat, setAktiveKat] = useState(null);

  useEffect(() => {
    const unsub = subscribeDokumente(setDokumente, () => setDokumente([]));
    return unsub;
  }, []);

  const cvflowStellen = stellen.filter(s => (s.quelle || 'cvflow') !== 'extern');

  const countFor = (id) => {
    const uploaded = dokumente.filter(d => d.kategorie === id).length;
    if (id === 'cv') return uploaded + cvflowStellen.filter(s => s.cv_data || s.cv_rohtext).length;
    if (id === 'anschreiben') return uploaded + cvflowStellen.filter(s => s.anschreiben || s.anschreiben_rohtext).length;
    return uploaded;
  };

  const filledCount = KATEGORIEN.filter(k => countFor(k.id) > 0).length;
  const totalDocs = dokumente.length + cvflowStellen.filter(s => s.cv_data || s.cv_rohtext || s.anschreiben || s.anschreiben_rohtext).length;

  const aktiveKatObj = KATEGORIEN.find(k => k.id === aktiveKat);

  return (
    <div style={{
      position: 'absolute', inset: 0,
      background: `radial-gradient(130% 90% at 20% 0%, #FBF7ED 0%, ${C.cream} 55%, #EDE6D6 100%)`,
      overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      fontFamily: C.fontBody,
    }}>
      <div style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', paddingBottom: 32 }}>

        {/* Topbar crumb */}
        <div style={{ padding: '0 20px 6px', display: 'flex', alignItems: 'center', gap: 7 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.amber }} />
          <span style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.muted, fontWeight: 600 }}>
            {nutzer?.displayName?.split(' ')[0] || 'Meine'}s Mappe
          </span>
        </div>

        {/* Header */}
        <div style={{ padding: '16px 20px 14px' }}>
          <h1 style={{ margin: 0, fontFamily: C.fontDisplay, fontSize: 38, lineHeight: 1.05, fontWeight: 400, color: C.ink, letterSpacing: '-0.01em' }}>
            Deine <em style={{ fontStyle: 'italic', color: C.amber }}>Unterlagen.</em>
          </h1>
          <div style={{ marginTop: 8, fontSize: 13, color: C.muted, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span><b style={{ color: C.ink, fontWeight: 600 }}>{filledCount} von {KATEGORIEN.length}</b> Kategorien gefüllt</span>
            <span style={{ color: C.faint }}>·</span>
            <span>{totalDocs} Dokument{totalDocs !== 1 ? 'e' : ''}</span>
          </div>
          <div style={{ marginTop: 12 }}>
            <ProgressChip total={KATEGORIEN.length} filled={filledCount} />
          </div>
        </div>

        {/* Cards */}
        <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Featured: Lebensläufe */}
          {KATEGORIEN.filter(k => k.size === 'featured').map(k => (
            <KatCard key={k.id} kat={k} count={countFor(k.id)} onPress={() => setAktiveKat(k.id)} />
          ))}

          {/* Wide: Anschreiben */}
          {KATEGORIEN.filter(k => k.id === 'anschreiben').map(k => (
            <KatCard key={k.id} kat={k} count={countFor(k.id)} onPress={() => setAktiveKat(k.id)} />
          ))}

          {/* 2×2 grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {KATEGORIEN.filter(k => k.size === 'tile').map(k => (
              <KatCard key={k.id} kat={k} count={countFor(k.id)} onPress={() => setAktiveKat(k.id)} />
            ))}
          </div>

          {/* Wide: Sonstige */}
          {KATEGORIEN.filter(k => k.id === 'sonstige').map(k => (
            <KatCard key={k.id} kat={k} count={countFor(k.id)} onPress={() => setAktiveKat(k.id)} />
          ))}
        </div>

        {/* Watermark */}
        <div style={{ textAlign: 'center', marginTop: 32, userSelect: 'none', pointerEvents: 'none' }}>
          <img src="/logo.png" alt="" style={{ width: 180, height: 'auto', opacity: 0.07, filter: 'grayscale(1)' }} draggable={false} />
        </div>
      </div>

      {/* Bottom sheet for category detail */}
      <MobileBottomSheet isOpen={!!aktiveKat} onClose={() => setAktiveKat(null)} initialSnap={1}>
        {aktiveKatObj && (
          <KatSheet
            kat={aktiveKatObj}
            dokumente={dokumente}
            stellen={cvflowStellen}
            onClose={() => setAktiveKat(null)}
            onUpload={uploadDokument}
            onStelleOeffnen={(s) => { setAktiveKat(null); onStelleOeffnen?.(s); }}
          />
        )}
      </MobileBottomSheet>
    </div>
  );
}
